import { useDispatch, useSelector } from "react-redux";
import { selectSideBySideValue } from "state/selections/selector";
import {
  selectData,
  selectDefinitions,
  selectFetchingData,
  selectFetchingSbsData,
  selectSbsData,
} from "./selectors";
import SelectionState from "state/selections/SelectionState";
import SummaryType from "state/selections/Constants/SummaryType";
import React, { useMemo } from "react";
import { deleteColumn } from "state/grid/actions";
import { titleCase } from "@ls/js-utils";
import { useQuery } from "react-query";
import { collectEntitySummaryInfo, splitByCalcType } from "state/util";
import { exchange } from "@ls/js-fetch";

export const useRatingOptionsQuery = () => {
  return useQuery(["qualityRatingFields"], async () => {
    const ratingOptions = (
      await exchange({
        baseURL: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
        url: "qualityRatingFields",
        params: { appName: process.env.REACT_APP_ID },
      })
    )?.data;
    return {
      ratingOptions,
      ratingOptionsByRatingId: ratingOptions.reduce((map, ratingOption) => {
        map[ratingOption.id] = ratingOption;
        return map;
      }, {}),
    };
  });
};

export const useRatingModesQuery = () => {
  return useQuery(["qualityRatingModes"], async () => {
    const ratingModes = (
      await exchange({
        baseURL: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
        url: "qualityRatingModes",
        params: { appName: process.env.REACT_APP_ID },
      })
    )?.data;

    return ratingModes?.map((value) => ({ label: titleCase(value), value }));
  });
};

export const useRatingMetricsQuery = () => {
  return useQuery(["qualityRatingMetrics"], async () => {
    const ratingMetrics = (
      await exchange({
        baseURL: process.env.REACT_APP_BASE_URL_GROUPING_SERVICE,
        url: "qualityRatingMetrics",
        params: { appName: process.env.REACT_APP_ID },
      })
    )?.data;

    return ratingMetrics?.map((value) => ({ label: titleCase(value), value }));
  });
};

export const useColumnDefinitions = () => {
  const definitions = useSelector(selectDefinitions(SelectionState.COMMITTED));
  const fetchingAbsData = useSelector(selectFetchingData(SummaryType.ABSOLUTE));
  const fetchingAbsSbsData = useSelector(
    selectFetchingSbsData(SummaryType.ABSOLUTE),
  );
  const sideBySide = useSelector(
    selectSideBySideValue(SelectionState.COMMITTED),
  );

  const dispatch = useDispatch();
  return React.useMemo(() => {
    return definitions.reduce((colDefs, definition) => {
      const { ratingId, analytic, summary, ratingMode, ratingMetric, key } =
        definition; //GS uses ratingId as its text key and analytic field as its numeric key
      const colDef = {
        loading: fetchingAbsData,
        field: [
          "ratings",
          analytic,
          summary,
          ratingMode.value,
          ratingMetric.value,
        ].join(","),
        colId: key,
        headerName: `${titleCase(ratingId)} ${titleCase(summary)} ${titleCase(
          ratingMode.value,
        )} ${titleCase(ratingMetric.value)}`,
        X_onDelete: () => dispatch(deleteColumn(definition)),
        // comparator: (valueA, valueB, nodeA, nodeB) => {
        //   let valA = nodeA ? nodeA.data[numericColId] : valueA;
        //   let valB = nodeB ? nodeB.data[numericColId] : valueB;
        //   if (valA === 0 || valA === undefined) {
        //     //sometimes ratings are 0 or null. treat these as same
        //     valA = null;
        //   }
        //   if (valB === 0 || valB === undefined) {
        //     valB = null;
        //   }
        //   if (valA === valB) {
        //     return 0;
        //   }
        //   if (!valA) {
        //     return -1;
        //   }
        //   if (!valB) {
        //     return 1;
        //   }
        //   return valA > valB ? 1 : -1;
        // }
      };
      colDefs.push(colDef);

      if (sideBySide) {
        const sbsColDef = {
          ...colDef,
          headerName: colDef.headerName + " - scenario",
          colId: colDef.colId + " - scenario",
          field: colDef.field + " - scenario",
          loading: fetchingAbsSbsData,
          X_sbs: true,
          X_originalColId: colDef.colId,
          X_backgroundColor: "#fffb95",
        };
        colDefs.push(sbsColDef);
      }
      return colDefs;
    }, []);
  }, [definitions, dispatch, fetchingAbsData, fetchingAbsSbsData, sideBySide]);
};

export const useRowsMap = () => {
  const definitions = useSelector(selectDefinitions(SelectionState.COMMITTED));
  const data = useSelector(selectData(SummaryType.ABSOLUTE));
  const sbsData = useSelector(selectSbsData(SummaryType.ABSOLUTE));
  const sideBySide = useSelector(
    selectSideBySideValue(SelectionState.COMMITTED),
  );

  return useMemo(() => {
    const entitySummaries = data?.root?.entitySummaries ?? [];
    const sbsEntitySummaries = sbsData?.root?.entitySummaries ?? [];
    const { entitySummaryMap } = collectEntitySummaryInfo(entitySummaries);
    const [absDefinitions, , bmkDefinitions] = splitByCalcType(definitions);
    const entityRowsMap = {};

    const handleDefs = (definitions, entitySummaries, isSbs) => {
      definitions.forEach((definition) => {
        const { ratingId, analytic, summary, ratingMode, ratingMetric } =
          definition;
        //Note: GS rating metric types, when requesting, are upper case (RATING_NUMERIC) but in response rating object, they're camel case (ratingNumeric)
        const ratingMetricResponseField = ratingMetric.value
          .toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase()); //converts HELLO_WORLD to helloWorld
        const field = [
          "ratings",
          analytic,
          summary,
          ratingMode.value,
          `${ratingMetric.value}${isSbs ? " - scenario" : ""}`,
        ].join(",");
        entitySummaries.forEach((entitySummary) => {
          const { qualityRatings = [], entityResultMetaData } = entitySummary;
          const { entityId } = entityResultMetaData;

          if (!entityRowsMap[entityId]) {
            entityRowsMap[entityId] = {};
          }
          const qualityRatingObj =
            qualityRatings.find((gsRatingObj) => {
              return (
                gsRatingObj.mode === ratingMode.value &&
                gsRatingObj.type === ratingId
              );
            }) ?? {};

          entityRowsMap[entityId][field] =
            qualityRatingObj[ratingMetricResponseField];
        });
      });
    };

    /**Handle abs and rel definitions**/
    handleDefs(absDefinitions, entitySummaries, false);

    /**handle side by side data, if enabled**/
    if (sideBySide) {
      handleDefs(absDefinitions, sbsEntitySummaries, true);
    }

    /**now handle benchmark definitions**/
    bmkDefinitions.forEach((definition) => {
      const { ratingId, analytic, summary, ratingMode, ratingMetric } =
        definition;
      const field = [
        "ratings",
        analytic,
        summary,
        ratingMode.value,
        ratingMetric.value,
      ].join(",");
      const ratingMetricResponseField = ratingMetric.value
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase()); //converts HELLO_WORLD to helloWorld
      entitySummaries.forEach((entitySummary) => {
        const { entityId, benchId } = entitySummary.entityResultMetaData;
        const { qualityRatings = [] } = entitySummaryMap.get(benchId) ?? {};
        if (!entityRowsMap?.[entityId]?.[field]) {
          if (!entityRowsMap?.[entityId]) {
            entityRowsMap[entityId] = {};
          }
          const qualityRatingObj =
            qualityRatings.find((gsRatingObj) => {
              return (
                gsRatingObj.mode === ratingMode.value &&
                gsRatingObj.type === ratingId
              );
            }) ?? {};
          //Note: GS rating metric types, when requesting, are upper case (RATING_NUMERIC) but in response rating object, they're camel case (ratingNumeric)

          entityRowsMap[entityId][field] =
            qualityRatingObj[ratingMetricResponseField];
        }
      });
    });
    return entityRowsMap;
  }, [
    data?.root?.entitySummaries,
    definitions,
    sbsData?.root?.entitySummaries,
    sideBySide,
  ]);
};
